import { MAX_WEBP_DIMENSION, uploadButtonLabel } from '@constants'
import {
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  PencilIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/joy'
import { colors, globalStyles } from '@styles/styles'
import { getImageAspectRatio, isValidAspectRatio, sizeInKB, sizeInMB, sizeToBytes } from '@utils'
import React, { useEffect, useState } from 'react'
import imageCompression from 'browser-image-compression'
import { InfoTooltip } from './InfoTooltip'

interface Props {
  onChange: (image: File[] | null) => void
  multiple?: boolean
  disabled?: boolean
  acceptedTypes?: string
  accept?: string
  maxFileSize?: number
  label?: React.ReactNode
  tooltip?: React.ReactNode
  acceptAspectRatio?: string
  aspectRatioTolerance?: number
  validateDimensions?: boolean
  iconButton?: boolean
  type?: 'file' | 'image'
}

const styles = {
  dropArea: {
    color: globalStyles.secondaryTextColor,
    border: globalStyles.dashedBorder,
    borderRadius: 8,
    p: 3,
    textAlign: 'center',
    my: '0.5rem',
    text: { color: colors.secondaryText },
  },
  button: { mt: 1 },
  sizeAlertModal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    container: {
      maxWidth: 500,
      p: 3,
    },
    closeButton: { mt: 3, float: 'right' },
  },
}

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`

const FileUploadInput = ({
  onChange,
  acceptedTypes,
  multiple,
  disabled,
  maxFileSize = Infinity,
  label,
  tooltip,
  accept = '',
  acceptAspectRatio,
  aspectRatioTolerance = 0.2,
  validateDimensions,
  iconButton,
  type = 'file',
}: Props) => {
  const [validFilesFromUpload, setValidFilesFromUpload] = useState<File[]>([])
  const [compressedValidFiles, setCompressedValidFiles] = useState<File[]>([])
  const [compressebleFiles, setCompressebleFiles] = useState<File[]>([])
  const [showSizeAlert, setShowSizeAlert] = useState<false | File[]>(false)

  const noSpacesAccept = accept.replace(/\s/g, '').replace(/\./g, '').toLowerCase()
  const acceptArray = noSpacesAccept.split(',').filter(Boolean)

  useEffect(() => {
    const combinedFiles = [...validFilesFromUpload, ...compressedValidFiles]
    if (combinedFiles.length === 0) return

    const uniqueFiles = combinedFiles.reduce((acc: File[], current: File) => {
      const exists = acc.some(
        (file) =>
          file.name === current.name && file.size === current.size && file.lastModified === current.lastModified,
      )
      return exists ? acc : [...acc, current]
    }, [])

    onChange(uniqueFiles)
  }, [validFilesFromUpload, compressedValidFiles])

  const isValidFile = ({
    file,
    aspectRatio,
    width,
    height,
    skipSizeCheck = false,
    skipDimensionsCheck = false,
  }: {
    file: File
    aspectRatio: string
    width: number
    height: number
    skipSizeCheck?: boolean
    skipDimensionsCheck?: boolean
  }) => {
    const validSize = skipSizeCheck ? true : file.size <= maxFileSize

    const fileExtension = file.name.split('.').pop() ?? ''
    const validType = accept ? acceptArray.includes(fileExtension.toLocaleLowerCase()) : true

    const validAspectRatio = acceptAspectRatio
      ? isValidAspectRatio({ aspectRatio, validAspectRatio: acceptAspectRatio, tolerance: aspectRatioTolerance })
      : true

    const isImage = file.type.startsWith('image/')
    const validDimensions =
      skipDimensionsCheck || !isImage
        ? true
        : width > 0 && height > 0 && width <= MAX_WEBP_DIMENSION && height <= MAX_WEBP_DIMENSION

    return validSize && validType && validAspectRatio && validDimensions
  }

  const sizeToString = (size: number) => {
    return size < sizeToBytes(1, 'MB') ? `${sizeInKB(size)}KB` : `${sizeInMB(size)}MB`
  }

  const handleFileUpload = async (e: any) => {
    const files = Array.from(e?.target?.files ?? []) as File[]

    const filesWithAspectRatio = await Promise.all(
      files.map(async (file) => {
        const { aspectRatio, width, height } = await getImageAspectRatio(file)
        return { file, aspectRatio, width, height }
      }),
    )

    const validFiles = filesWithAspectRatio.filter(isValidFile)

    const compressibleCandidates = filesWithAspectRatio.filter(
      (file) => isValidFile({ ...file, skipSizeCheck: true, skipDimensionsCheck: true }) && !isValidFile(file),
    )

    const compressibleFileList = compressibleCandidates.filter(
      (file) => file.file.size > maxFileSize || file.width > MAX_WEBP_DIMENSION || file.height > MAX_WEBP_DIMENSION,
    )

    const invalidFileList = filesWithAspectRatio.filter((file) => !isValidFile(file))

    if (invalidFileList.length > 0) {
      setShowSizeAlert(invalidFileList.map(({ file }) => file))
    }

    if (compressibleFileList.length > 0) {
      setCompressebleFiles(compressibleFileList.map(({ file }) => file))
    } else {
      setCompressebleFiles([])
    }

    if (validFiles.length > 0) {
      setValidFilesFromUpload(validFiles.map(({ file }) => file))
      setCompressedValidFiles([])
    }
  }

  const [isCompressingAll, setIsCompressingAll] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState<number>(0)

  const MAX_FILE_WIDTH_HEIGHT = 1920

  const filesToCompress = showSizeAlert !== false && showSizeAlert.length ? showSizeAlert : []

  const handleCompressAll = async () => {
    if (!showSizeAlert || !showSizeAlert.length) return

    if (!filesToCompress.length) {
      setTimeout(() => {
        setShowSizeAlert(false)
      }, 500)
      return
    }

    setIsCompressingAll(true)
    setCompressionProgress(0)

    setValidFilesFromUpload([])
    setCompressedValidFiles([])

    try {
      const BATCH_SIZE = 4
      const totalFiles = filesToCompress.length
      let processedFiles = 0

      const batches = []
      for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
        batches.push(filesToCompress.slice(i, i + BATCH_SIZE))
      }

      let compressedResults: (File | null)[] = []

      const maxSizeMB = maxFileSize / (1024 * 1024)

      for (const [_index, batch] of batches.entries()) {
        const batchPromises = batch.map((originalFile) =>
          imageCompression(originalFile, {
            maxSizeMB: maxSizeMB,
            maxWidthOrHeight: MAX_FILE_WIDTH_HEIGHT,
            useWebWorker: true,
          }).then(async (compressedBlob) => {
            const compressedFile = new File([compressedBlob], originalFile.name, {
              type: compressedBlob.type || originalFile.type,
              lastModified: Date.now(),
            })
            const { aspectRatio, width, height } = await getImageAspectRatio(compressedFile)
            if (isValidFile({ file: compressedFile, aspectRatio, width, height })) {
              return compressedFile
            }
            return null
          }),
        )

        const batchResults = await Promise.all(batchPromises)
        compressedResults = [...compressedResults, ...batchResults]

        processedFiles += batch.length
        const progress = (processedFiles / totalFiles) * 100
        setCompressionProgress(Math.min(progress, 100))
      }

      const validCompressedFiles = compressedResults.filter(Boolean) as File[]

      setValidFilesFromUpload((prev) => prev.filter((f) => !validCompressedFiles.some((cf) => cf.name === f.name)))

      setCompressedValidFiles((prev) => [
        ...prev.filter((f) => !validCompressedFiles.some((cf) => cf.name === f.name)),
        ...validCompressedFiles,
      ])

      setShowSizeAlert((prev) =>
        prev ? prev.filter((f) => !validCompressedFiles.some((cf) => cf.name === f.name)) : false,
      )
    } catch (error) {
      console.error('Error en compresión masiva:', error)
    } finally {
      setIsCompressingAll(false)
      setCompressionProgress(0)
      setTimeout(() => {
        setShowSizeAlert(false)
      }, 500)
    }
  }
  const hasCompressibleFiles = compressebleFiles.some((file) => file.type.startsWith('image/'))

  return (
    <>
      {!iconButton ? (
        <>
          {label && (
            <Stack
              direction="row"
              gap={1}
            >
              <Typography
                level="body-md-light"
                color="neutral"
              >
                {label}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip}>
                  <QuestionMarkCircleIcon
                    style={{
                      color: 'rgba(var(--joy-palette-neutral-mainChannel) / 1)',
                    }}
                    width={'1em'}
                  />
                </Tooltip>
              )}
            </Stack>
          )}
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              handleFileUpload({ target: { files: Array.from(e.dataTransfer.items).map((item) => item.getAsFile()) } })
            }}
            sx={styles.dropArea}
          >
            <UploadButton
              handleFileUpload={handleFileUpload}
              acceptedTypes={accept ? accept : acceptedTypes ?? ''}
              multiple={multiple}
              disabled={disabled}
              type={type}
            />
          </Box>
        </>
      ) : (
        <IconButton
          disabled={disabled}
          component="label"
          variant="plain"
          role={undefined}
        >
          <PencilIcon width={'1em'} />
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileUpload}
            accept={accept ? accept : acceptedTypes ?? ''}
            multiple={multiple}
          />
        </IconButton>
      )}
      <Modal
        open={Boolean(showSizeAlert)}
        onClose={() => setShowSizeAlert(false)}
        sx={styles.sizeAlertModal}
      >
        <ModalDialog sx={{ maxWidth: '360px', padding: '45px' }}>
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ExclamationCircleIcon
              strokeWidth="1"
              style={{
                width: '86px',
                color: 'var(--joy-palette-danger-plainColor)',
              }}
            />
            <Typography
              level="h2"
              sx={{ textAlign: 'center' }}
            >
              {hasCompressibleFiles
                ? `¡Ups! Alguna de las imágenes no pudieron ser cargadas`
                : `¡Ups! Alguno de los archivos no pudieron ser cargados`}
            </Typography>
            <Stack gap={2}>
              <Stack sx={{ maxWidth: '100%' }}>
                <Typography level="title-lg-bold">
                  {hasCompressibleFiles
                    ? 'El tamaño o la resolución de la imagen no están permitidos (máx. 2MB / 3000×3000 px):'
                    : `Los siguientes archivos no pudieron ser cargados:`}
                </Typography>
                {showSizeAlert !== false && (
                  <Stack
                    sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                    }}
                  >
                    {showSizeAlert.map((file, index) => (
                      <Typography
                        level="body-md-light"
                        color="neutral"
                        key={index}
                        sx={{
                          whiteSpace: 'normal',
                        }}
                      >
                        {file.name}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
              <Stack>
                {hasCompressibleFiles ? (
                  <Typography level="title-lg-bold">
                    Puedes reducir las imagenes automáticamente. Ten en cuenta que la calidad podría verse afectada.{' '}
                  </Typography>
                ) : (
                  <>
                    <Typography level="title-lg-bold">
                      Recuerde que los archivos deben cumplir con los requerimientos:
                    </Typography>
                    {maxFileSize !== Infinity && (
                      <Typography
                        level="body-md-light"
                        color="neutral"
                      >
                        Peso máximo por archivo: {sizeToString(maxFileSize)}
                      </Typography>
                    )}
                    {Boolean(accept) && (
                      <Typography
                        level="body-md-light"
                        color="neutral"
                      >
                        Formatos: {accept}
                      </Typography>
                    )}
                    {Boolean(acceptAspectRatio) && (
                      <Typography
                        level="body-md-light"
                        color="neutral"
                      >
                        Relación de aspecto: {`${acceptAspectRatio} (± ${aspectRatioTolerance * 100}%)`}
                      </Typography>
                    )}
                    {validateDimensions && (
                      <Typography
                        level="body-md-light"
                        color="neutral"
                      >
                        Dimensiones máximas: {MAX_WEBP_DIMENSION}x{MAX_WEBP_DIMENSION}
                      </Typography>
                    )}
                  </>
                )}

                {hasCompressibleFiles && (
                  <Button
                    onClick={handleCompressAll}
                    disabled={isCompressingAll}
                    sx={{ mt: 2 }}
                    startDecorator={isCompressingAll ? <CircularProgress size="sm" /> : null}
                    endDecorator={
                      !isCompressingAll ? (
                        <InfoTooltip text="Para una tiempo de compresión efectivo recomendamos un maximo de 20 imágenes" />
                      ) : null
                    }
                  >
                    {isCompressingAll ? `Procesando... (${Math.round(compressionProgress)}%)` : 'Reducir imágenes'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
          <DialogContent></DialogContent>
          <Button
            onClick={() => {
              setShowSizeAlert(false)
            }}
            sx={styles.sizeAlertModal.closeButton}
          >
            Cerrar
          </Button>
        </ModalDialog>
      </Modal>
    </>
  )
}

interface UploadButtonProps {
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
  acceptedTypes: string
  multiple?: boolean
  disabled?: boolean
  type?: 'file' | 'image'
}

const UploadButton = ({ handleFileUpload, acceptedTypes, multiple, disabled, type = 'file' }: UploadButtonProps) => (
  <Button
    component="label"
    tabIndex={-1}
    disabled={disabled}
    variant="plain"
    color="neutral"
    startDecorator={<CloudArrowUpIcon />}
    sx={{
      '&:hover': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    }}
  >
    <Typography
      level="body-md-light"
      color="neutral"
      sx={{ mr: 0.5 }}
    >
      {`Arrastre ${type === 'image' ? 'las imágenes' : 'los archivos'} aquí o`}
    </Typography>
    <Typography
      color="primary"
      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {uploadButtonLabel(type)}
    </Typography>
    <VisuallyHiddenInput
      type="file"
      onChange={handleFileUpload}
      accept={acceptedTypes}
      multiple={multiple}
    />
  </Button>
)
export default FileUploadInput
